import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { sopsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import SOPQuizModal from '../components/SOPQuizModal';
import { 
  Plus, 
  BookOpen, 
  CheckCircle,
  FileText,
  Edit2,
  Trash2,
  Eye,
  Save,
  Search,
  ExternalLink,
  Upload,
  FileQuestion,
  Lock,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const JOB_ROLES = [
  "Clin Ops",
  "Reg Ops", 
  "Quality Ops",
  "Recr Ops",
  "eSource Ops",
  "Data Ops",
  "Budget Ops"
];

const SOPLibrary = () => {
  const { canManage, user } = useAuth();
  const [sops, setSOPs] = useState([]);
  const [completions, setCompletions] = useState([]);
  const [quizStatuses, setQuizStatuses] = useState({}); // Track quiz pass status per SOP
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showSOPEditor, setShowSOPEditor] = useState(false);
  const [showSOPViewer, setShowSOPViewer] = useState(false);
  const [showQuizModal, setShowQuizModal] = useState(false);
  const [selectedSOP, setSelectedSOP] = useState(null);
  const [sopForm, setSOPForm] = useState({
    title: '',
    sop_number: '',
    content: '',
    category: '',
    version: '1.0',
    document_url: '',
    role_requirements: {},
  });

  const categories = [
    'General Administration',
    'Document Management',
    'Training',
    'Study Startup',
    'Study Conduct',
    'Study Closeout',
  ];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [sopsRes, completionsRes] = await Promise.all([
        sopsAPI.getAll(),
        sopsAPI.getMyCompletions()
      ]);
      setSOPs(sopsRes.data);
      setCompletions(completionsRes.data);
      
      // Load quiz status for each SOP that has a quiz
      const sopsWithQuizzes = sopsRes.data.filter(s => s.quiz_id);
      const quizStatusPromises = sopsWithQuizzes.map(async (sop) => {
        try {
          const quizRes = await sopsAPI.getQuiz(sop.id);
          return { sopId: sop.id, passed: quizRes.data.passed, hasQuiz: !!quizRes.data.quiz };
        } catch (e) {
          return { sopId: sop.id, passed: false, hasQuiz: false };
        }
      });
      
      const statuses = await Promise.all(quizStatusPromises);
      const statusMap = {};
      statuses.forEach(s => {
        statusMap[s.sopId] = { passed: s.passed, hasQuiz: s.hasQuiz };
      });
      setQuizStatuses(statusMap);
    } catch (error) {
      console.error('Failed to load SOPs:', error);
      toast.error('Failed to load SOPs');
    } finally {
      setLoading(false);
    }
  };

  const isCompleted = (sopId) => {
    return completions.some(c => c.sop_id === sopId);
  };

  const getRequirementForRole = (sop, role) => {
    if (!sop.role_requirements) return '';
    return sop.role_requirements[role] || '';
  };

  const getUserJobRole = () => {
    return user?.job_role || 'Clin Ops';
  };

  const handleCreateSOP = () => {
    setSelectedSOP(null);
    setSOPForm({
      title: '',
      sop_number: '',
      content: '',
      category: 'General Administration',
      version: '1.0',
      document_url: '',
      role_requirements: JOB_ROLES.reduce((acc, role) => ({ ...acc, [role]: 'M' }), {}),
    });
    setShowSOPEditor(true);
  };

  const handleEditSOP = (sop) => {
    setSelectedSOP(sop);
    setSOPForm({
      title: sop.title,
      sop_number: sop.sop_number || '',
      content: sop.content,
      category: sop.category,
      version: sop.version,
      document_url: sop.document_url || '',
      role_requirements: sop.role_requirements || {},
    });
    setShowSOPEditor(true);
  };

  const handleViewSOP = (sop) => {
    setSelectedSOP(sop);
    setShowSOPViewer(true);
  };

  const handleOpenQuiz = (sop) => {
    setSelectedSOP(sop);
    setShowQuizModal(true);
  };

  const handleQuizPassed = (sopId) => {
    // Update quiz status
    setQuizStatuses(prev => ({
      ...prev,
      [sopId]: { ...prev[sopId], passed: true }
    }));
    // Also mark as complete
    setCompletions(prev => [...prev, { sop_id: sopId }]);
    setShowQuizModal(false);
  };

  const hasQuiz = (sop) => {
    return !!sop.quiz_id || quizStatuses[sop.id]?.hasQuiz;
  };

  const quizPassed = (sop) => {
    return quizStatuses[sop.id]?.passed || false;
  };

  const handleSaveSOP = async () => {
    if (!sopForm.title.trim()) {
      toast.error('Please enter a title');
      return;
    }

    try {
      const dataToSave = {
        ...sopForm,
        title: sopForm.sop_number ? `${sopForm.sop_number} - ${sopForm.title.replace(/^[A-Z]+-\d+\s*-\s*/, '')}` : sopForm.title,
      };

      if (selectedSOP) {
        await sopsAPI.update(selectedSOP.id, dataToSave);
        toast.success('SOP updated');
      } else {
        await sopsAPI.create(dataToSave);
        toast.success('SOP created');
      }
      setShowSOPEditor(false);
      loadData();
    } catch (error) {
      console.error('Failed to save SOP:', error);
      toast.error('Failed to save SOP');
    }
  };

  const handleDeleteSOP = async (sopId) => {
    if (!window.confirm('Are you sure you want to delete this SOP?')) return;
    
    try {
      await sopsAPI.delete(sopId);
      toast.success('SOP deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete SOP');
    }
  };

  const handleMarkComplete = async () => {
    if (!selectedSOP) return;
    
    try {
      await sopsAPI.markComplete(selectedSOP.id);
      toast.success('SOP marked as read');
      setShowSOPViewer(false);
      loadData();
    } catch (error) {
      console.error('Failed to mark complete:', error);
      toast.error('Failed to mark complete');
    }
  };

  const handleRoleRequirementChange = (role, value) => {
    setSOPForm({
      ...sopForm,
      role_requirements: {
        ...sopForm.role_requirements,
        [role]: value,
      },
    });
  };

  const filteredSOPs = sops.filter(sop => {
    const matchesSearch = sop.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         sop.sop_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         sop.content?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || sop.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Group SOPs by category
  const groupedSOPs = filteredSOPs.reduce((acc, sop) => {
    const category = sop.category || 'General';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(sop);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
      </div>
    );
  }

  const userRole = getUserJobRole();

  return (
    <div className="space-y-6 animate-fade-in" data-testid="sops-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold text-slate-900 flex items-center gap-3">
            <BookOpen className="w-8 h-8 text-blue-600" />
            SOP Library
          </h1>
          <p className="text-slate-600 mt-1">
            Standard Operating Procedures - Read and acknowledge completion
          </p>
        </div>
        {canManage && (
          <Button 
            onClick={handleCreateSOP}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="create-sop-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add SOP
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card className="border-slate-200 shadow-card">
        <CardContent className="p-4">
          <div className="flex gap-4 flex-wrap">
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search SOPs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="sop-search-input"
              />
            </div>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-[200px]" data-testid="sop-category-filter">
                <SelectValue placeholder="Filter by category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* SOPs List by Category */}
      <div className="space-y-6">
        {Object.keys(groupedSOPs).length === 0 ? (
          <Card className="border-slate-200 shadow-card">
            <CardContent className="text-center py-12">
              <FileText className="w-16 h-16 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">
                {searchQuery || selectedCategory !== 'all' 
                  ? 'No SOPs match your filters' 
                  : 'No SOPs available'}
              </p>
              {canManage && !searchQuery && selectedCategory === 'all' && (
                <Button onClick={handleCreateSOP} className="mt-4">
                  Add your first SOP
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          Object.entries(groupedSOPs).map(([category, categorySOPs]) => (
            <Card key={category} className="border-slate-200 shadow-card">
              <CardHeader className="pb-3 border-b border-slate-100">
                <CardTitle className="text-lg font-heading flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-blue-500" />
                  {category}
                  <Badge variant="outline" className="ml-2">
                    {categorySOPs.length} SOPs
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y divide-slate-100">
                  {categorySOPs.map((sop) => {
                    const completed = isCompleted(sop.id);
                    const requirement = getRequirementForRole(sop, userRole);
                    const sopHasQuiz = hasQuiz(sop);
                    const sopQuizPassed = quizPassed(sop);
                    
                    return (
                      <div 
                        key={sop.id}
                        className={`flex items-center justify-between p-4 hover:bg-slate-50 transition-colors
                          ${completed ? 'bg-emerald-50/50' : ''}
                        `}
                        data-testid={`sop-item-${sop.id}`}
                      >
                        <div className="flex items-center gap-4 flex-1 min-w-0">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0
                            ${completed ? 'bg-emerald-100' : 'bg-slate-100'}
                          `}>
                            {completed ? (
                              <CheckCircle className="w-5 h-5 text-emerald-600" />
                            ) : (
                              <FileText className="w-5 h-5 text-slate-500" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              {sop.sop_number && (
                                <Badge variant="outline" className="text-xs font-mono">
                                  {sop.sop_number}
                                </Badge>
                              )}
                              <h3 className="font-medium text-slate-900">{sop.title.replace(/^[A-Z]+-\d+\s*-\s*/, '')}</h3>
                              {requirement === 'M' && (
                                <Badge className="bg-red-100 text-red-700 text-xs">Mandatory</Badge>
                              )}
                              {requirement === 'R' && (
                                <Badge variant="outline" className="text-slate-500 text-xs">Reference</Badge>
                              )}
                              {sopHasQuiz && (
                                <Badge className={`text-xs ${sopQuizPassed ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                                  <FileQuestion className="w-3 h-3 mr-1" />
                                  {sopQuizPassed ? 'Quiz Passed' : 'Quiz Required'}
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-xs text-slate-500">Version {sop.version}</span>
                              {sop.document_url && (
                                <Badge variant="outline" className="text-xs text-blue-600">
                                  <Upload className="w-3 h-3 mr-1" />
                                  Document attached
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2 ml-4">
                          {sop.document_url && (
                            <a href={sop.document_url} target="_blank" rel="noopener noreferrer">
                              <Button variant="outline" size="sm">
                                <ExternalLink className="w-4 h-4 mr-1" />
                                Open
                              </Button>
                            </a>
                          )}
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleViewSOP(sop)}
                            data-testid={`view-sop-${sop.id}`}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            Read
                          </Button>
                          {/* Quiz Button */}
                          {sopHasQuiz && !completed && (
                            <Button 
                              size="sm"
                              onClick={() => handleOpenQuiz(sop)}
                              className={sopQuizPassed ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-blue-600 hover:bg-blue-700'}
                              data-testid={`take-quiz-${sop.id}`}
                            >
                              <FileQuestion className="w-4 h-4 mr-1" />
                              {sopQuizPassed ? 'Quiz Passed' : 'Take Quiz'}
                            </Button>
                          )}
                          {canManage && (
                            <>
                              <Button 
                                variant="outline" 
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => handleEditSOP(sop)}
                                data-testid={`edit-sop-${sop.id}`}
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="outline" 
                                size="icon"
                                className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => handleDeleteSOP(sop.id)}
                                data-testid={`delete-sop-${sop.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                          {completed && (
                            <Badge className="bg-emerald-100 text-emerald-700">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Completed
                            </Badge>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* SOP Editor Dialog */}
      <Dialog open={showSOPEditor} onOpenChange={setShowSOPEditor}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {selectedSOP ? 'Edit SOP' : 'Add SOP'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>SOP Number</Label>
                <Input
                  value={sopForm.sop_number}
                  onChange={(e) => setSOPForm({ ...sopForm, sop_number: e.target.value })}
                  placeholder="e.g., GA-01, SM-02"
                  data-testid="sop-number-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Version</Label>
                <Input
                  value={sopForm.version}
                  onChange={(e) => setSOPForm({ ...sopForm, version: e.target.value })}
                  placeholder="V1.0"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Title</Label>
              <Input
                value={sopForm.title.replace(/^[A-Z]+-\d+\s*-\s*/, '')}
                onChange={(e) => setSOPForm({ ...sopForm, title: e.target.value })}
                placeholder="e.g., Development and Administration of Standard Operating Procedures"
                data-testid="sop-title-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Category</Label>
              <Select 
                value={sopForm.category}
                onValueChange={(v) => setSOPForm({ ...sopForm, category: v })}
              >
                <SelectTrigger data-testid="sop-category-select">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Document URL (Optional)</Label>
              <Input
                value={sopForm.document_url || ''}
                onChange={(e) => setSOPForm({ ...sopForm, document_url: e.target.value })}
                placeholder="https://sharepoint.com/... or link to document"
                data-testid="sop-document-url-input"
              />
              <p className="text-xs text-slate-500">Link to SharePoint, Google Drive, or other document location</p>
            </div>

            <div className="space-y-2">
              <Label>Content / Description</Label>
              <Textarea
                value={sopForm.content}
                onChange={(e) => setSOPForm({ ...sopForm, content: e.target.value })}
                placeholder="Enter the SOP content or summary..."
                rows={6}
                className="font-mono text-sm"
                data-testid="sop-content-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Role Requirements</Label>
              <div className="grid grid-cols-2 gap-2 p-3 bg-slate-50 rounded-lg">
                {JOB_ROLES.map(role => (
                  <div key={role} className="flex items-center justify-between">
                    <span className="text-sm text-slate-700">{role}</span>
                    <Select 
                      value={sopForm.role_requirements[role] || ''}
                      onValueChange={(v) => handleRoleRequirementChange(role, v)}
                    >
                      <SelectTrigger className="w-[120px] h-8">
                        <SelectValue placeholder="N/A" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="">N/A</SelectItem>
                        <SelectItem value="M">Mandatory</SelectItem>
                        <SelectItem value="R">Reference</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSOPEditor(false)}>Cancel</Button>
            <Button onClick={handleSaveSOP} className="bg-blue-600 hover:bg-blue-700" data-testid="save-sop-btn">
              <Save className="w-4 h-4 mr-2" />
              Save SOP
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SOP Viewer Dialog */}
      <Dialog open={showSOPViewer} onOpenChange={setShowSOPViewer}>
        <DialogContent className="sm:max-w-[800px] max-h-[90vh]">
          {selectedSOP && (
            <>
              <DialogHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      {selectedSOP.sop_number && (
                        <Badge variant="outline" className="font-mono">{selectedSOP.sop_number}</Badge>
                      )}
                      <Badge variant="outline">Version {selectedSOP.version}</Badge>
                    </div>
                    <DialogTitle className="font-heading text-xl">
                      {selectedSOP.title.replace(/^[A-Z]+-\d+\s*-\s*/, '')}
                    </DialogTitle>
                    <p className="text-sm text-slate-500 mt-1">{selectedSOP.category}</p>
                  </div>
                  {isCompleted(selectedSOP.id) && (
                    <Badge className="bg-emerald-100 text-emerald-700">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Completed
                    </Badge>
                  )}
                </div>
              </DialogHeader>
              
              {selectedSOP.document_url && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-blue-600" />
                    <span className="text-blue-700 font-medium">Document attached</span>
                  </div>
                  <a href={selectedSOP.document_url} target="_blank" rel="noopener noreferrer">
                    <Button size="sm" className="bg-blue-600 hover:bg-blue-700">
                      <ExternalLink className="w-4 h-4 mr-1" />
                      Open Document
                    </Button>
                  </a>
                </div>
              )}

              <ScrollArea className="h-[40vh] pr-4">
                <div className="prose prose-slate max-w-none">
                  <div className="whitespace-pre-wrap text-slate-700 leading-relaxed">
                    {selectedSOP.content}
                  </div>
                </div>
              </ScrollArea>

              {/* Ask a Question - Opens email client */}
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-700">Have a question about this SOP?</p>
                  <p className="text-xs text-slate-500">Contact your lead for clarification</p>
                </div>
                <a 
                  href={`mailto:?subject=Question about ${selectedSOP.sop_number || ''} - ${selectedSOP.title}&body=Hi,%0D%0A%0D%0AI have a question about the SOP "${selectedSOP.title}"%0D%0A%0D%0AMy question:%0D%0A%0D%0A`}
                  data-testid="ask-question-btn"
                >
                  <Button variant="outline" size="sm">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    Ask a Question
                  </Button>
                </a>
              </div>

              <DialogFooter className="flex justify-between">
                <Button variant="outline" onClick={() => setShowSOPViewer(false)}>
                  Close
                </Button>
                <div className="flex gap-2">
                  {/* Show quiz button if quiz exists and not passed */}
                  {hasQuiz(selectedSOP) && !quizPassed(selectedSOP) && (
                    <Button 
                      onClick={() => {
                        setShowSOPViewer(false);
                        handleOpenQuiz(selectedSOP);
                      }}
                      className="bg-blue-600 hover:bg-blue-700"
                      data-testid="start-quiz-btn"
                    >
                      <FileQuestion className="w-4 h-4 mr-2" />
                      Take Quiz to Complete
                    </Button>
                  )}
                  {/* Only allow manual completion if no quiz or quiz is passed */}
                  {!isCompleted(selectedSOP.id) && (!hasQuiz(selectedSOP) || quizPassed(selectedSOP)) && (
                    <Button 
                      onClick={handleMarkComplete}
                      className="bg-emerald-600 hover:bg-emerald-700"
                      data-testid="mark-complete-btn"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Mark as Read
                    </Button>
                  )}
                </div>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* SOP Quiz Modal */}
      <SOPQuizModal
        isOpen={showQuizModal}
        onClose={() => setShowQuizModal(false)}
        sop={selectedSOP}
        onQuizPassed={handleQuizPassed}
      />
    </div>
  );
};

export default SOPLibrary;
